/**
 * Sleepy Sloth Recently Viewed Products
 * Tracks and displays recently viewed products using localStorage
 * Privacy-compliant - data stored locally only, no server transmission
 */

class RecentlyViewedManager {
  constructor() {
    this.storageKey = 'sleepy_sloth_recently_viewed';
    this.maxItems = 10; // Track up to 10 products
    this.recentProducts = this.loadRecentProducts();
  }

  /**
   * Load recently viewed products from localStorage
   */
  loadRecentProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      return [];
    }
  }

  /**
   * Save recently viewed products to localStorage
   */
  saveRecentProducts() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.recentProducts));
      
      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('recentlyviewed:updated', {
        detail: { products: this.recentProducts }
      }));
    } catch (error) {
      console.error('Error saving recently viewed products:', error);
    }
  }

  /**
   * Add product to recently viewed
   * @param {Object} product - Product data to track
   */
  addProduct(product) {
    if (!product || !product.id) {
      return;
    }

    // Remove product if it already exists (to move it to the front)
    this.recentProducts = this.recentProducts.filter(p => p.id !== product.id);

    // Add product to the beginning of the array
    this.recentProducts.unshift({
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: product.image,
      hoverImage: product.hoverImage,
      featuredImageAlt: product.featuredImageAlt,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      url: product.url,
      available: product.available,
      options: product.options,
      variants: product.variants,
      viewedAt: new Date().toISOString()
    });

    // Limit to max items
    if (this.recentProducts.length > this.maxItems) {
      this.recentProducts = this.recentProducts.slice(0, this.maxItems);
    }

    this.saveRecentProducts();
  }

  /**
   * Get recently viewed products
   * @param {number} limit - Number of products to return
   * @param {string} excludeId - Product ID to exclude (typically current product)
   * @returns {Array} Array of recently viewed products
   */
  getProducts(limit = 4, excludeId = null) {
    let products = [...this.recentProducts];

    // Exclude specific product if provided
    if (excludeId) {
      products = products.filter(p => p.id !== excludeId);
    }

    // Return limited number
    return products.slice(0, limit);
  }

  /**
   * Clear all recently viewed products
   */
  clearAll() {
    this.recentProducts = [];
    this.saveRecentProducts();
    
    // Dispatch event for UI notification
    window.dispatchEvent(new CustomEvent('recentlyviewed:cleared'));
  }

  /**
   * Get count of recently viewed products
   */
  getCount(excludeId = null) {
    if (excludeId) {
      return this.recentProducts.filter(p => p.id !== excludeId).length;
    }
    return this.recentProducts.length;
  }
}

// Initialize manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.recentlyViewedManager = new RecentlyViewedManager();
  });
} else {
  window.recentlyViewedManager = new RecentlyViewedManager();
}
